import React, { useState, useEffect } from "react";
import api from '../Services/api'

interface Log {
  userId: string;
  userEmail: string;
  type: string;
  event: string;
  details: any;
  timestamp: string;
}

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalLogs: number;
  logsPerPage: number;
}

export const AdminLogs = () => {
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [paginationData, setPaginationData] = useState<PaginationData | null>(null);
  const [type, setType] = useState<string>("");
  const [limit, setLimit] = useState<number>(25);
  const [userSearch, setUserSearch] = useState<string>("");

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await api.get(`/logs/admin`, {
        params: {
          page,
          type,
          limit,
          userSearch
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(data.logs);
      setPaginationData(data.pagination);
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, type, limit, userSearch]);

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">System Logs</h2>

      <div className="mb-4 flex gap-4 items-center">
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="p-2 border rounded bg-white dark:bg-neutral-dark text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
        >
          <option value="">All Types</option>
          <option value="auth">Authentication</option>
          <option value="subscription">Subscription</option>
          <option value="classification">Classification</option>
        </select>

        <select
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          className="p-2 border rounded bg-white dark:bg-neutral-dark text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
        >
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
          <option value={100}>100 per page</option>
        </select>

        <input
          type="text"
          placeholder="Search by user email"
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          className="p-2 border rounded bg-white dark:bg-neutral-dark text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
        />
      </div>

      {loading ? (
        <div className="text-gray-900 dark:text-gray-100">Loading logs...</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white dark:bg-neutral-dark shadow-md rounded-lg">
              <thead className="bg-gray-50 dark:bg-neutral-dark">
                <tr>
                  <th className="px-6 py-3 text-left text-gray-900 dark:text-gray-100">Timestamp</th>
                  <th className="px-6 py-3 text-left text-gray-900 dark:text-gray-100">User</th>
                  <th className="px-6 py-3 text-left text-gray-900 dark:text-gray-100">Type</th>
                  <th className="px-6 py-3 text-left text-gray-900 dark:text-gray-100">Event</th>
                  <th className="px-6 py-3 text-left text-gray-900 dark:text-gray-100">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {logs.map((log, index) => (
                  <tr key={index} className="border-b border-gray-200 dark:border-gray-700">
                    <td className="px-6 py-4 text-gray-900 dark:text-gray-100">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-gray-900 dark:text-gray-100">{log.userEmail}</td>
                    <td className="px-6 py-4 text-gray-900 dark:text-gray-100">{log.type}</td>
                    <td className="px-6 py-4 text-gray-900 dark:text-gray-100">{log.event}</td>
                    <td className="px-6 py-4">
                      <pre className="whitespace-pre-wrap text-sm text-gray-900 dark:text-gray-100">
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {paginationData && (
            <div className="mt-4 flex justify-between items-center text-gray-900 dark:text-gray-100">
              <div>
                Page {paginationData.currentPage} of {paginationData.totalPages}
              </div>
              <div className="space-x-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 bg-gray-200 dark:bg-neutral-light rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page === paginationData.totalPages}
                  className="px-4 py-2 bg-gray-200 dark:bg-neutral-light rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
